"use client";

import {
  ErrorNotice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  CARD,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { WeekdayToggles } from "@/components/tools/study/controls";

/*
 * The class schedule maker's add/edit form. The widget owns the draft; this draws it.
 * Every word comes in through `labels`, which the widget passes its strings as.
 */

export interface ClassFormLabels {
  addHeading: string;
  editHeading: string;
  nameLabel: string;
  namePlaceholder: string;
  locationLabel: string;
  locationPlaceholder: string;
  classDaysLabel: string;
  weekdaysShort: readonly string[];
  startLabel: string;
  endLabel: string;
  colorLabel: string;
  colors: readonly string[];
  addClass: string;
  saveClass: string;
  cancelEdit: string;
}

export interface Draft {
  id: string | null;
  name: string;
  days: boolean[];
  start: string;
  end: string;
  location: string;
  color: string;
}

export const EMPTY_DRAFT: Draft = {
  id: null,
  name: "",
  days: [false, false, false, false, false, false, false],
  start: "09:00",
  end: "10:00",
  location: "",
  color: "0",
};

export function ClassForm({
  draft,
  error,
  labels,
  onChange,
  onSubmit,
  onCancel,
}: {
  draft: Draft;
  error: string | null;
  labels: ClassFormLabels;
  onChange: (draft: Draft) => void;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  return (
    <section className={`${CARD} space-y-4 print:hidden`}>
      <h2 className={WIDGET_H3}>
        {draft.id ? labels.editHeading : labels.addHeading}
      </h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="cs-name"
          label={labels.nameLabel}
          onChange={(name) => onChange({ ...draft, name })}
          placeholder={labels.namePlaceholder}
          value={draft.name}
        />
        <TextField
          id="cs-location"
          label={labels.locationLabel}
          onChange={(location) => onChange({ ...draft, location })}
          placeholder={labels.locationPlaceholder}
          value={draft.location}
        />
      </div>
      <WeekdayToggles
        labels={labels.weekdaysShort}
        legend={labels.classDaysLabel}
        name="cs-meets"
        onChange={(meets) => onChange({ ...draft, days: meets })}
        value={draft.days}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          id="cs-start"
          label={labels.startLabel}
          onChange={(start) => onChange({ ...draft, start })}
          type="time"
          value={draft.start}
        />
        <TextField
          id="cs-end"
          label={labels.endLabel}
          onChange={(end) => onChange({ ...draft, end })}
          type="time"
          value={draft.end}
        />
        <SelectField
          id="cs-color"
          label={labels.colorLabel}
          onChange={(color) => onChange({ ...draft, color })}
          options={labels.colors.map((label, index) => ({
            value: String(index),
            label,
          }))}
          value={draft.color}
        />
      </div>
      {error ? <ErrorNotice>{error}</ErrorNotice> : null}
      <div className="flex flex-wrap gap-3">
        <button className={BUTTON_PRIMARY} onClick={onSubmit} type="button">
          {draft.id ? labels.saveClass : labels.addClass}
        </button>
        {draft.id ? (
          <button
            className={BUTTON_SECONDARY}
            onClick={() => onCancel()}
            type="button"
          >
            {labels.cancelEdit}
          </button>
        ) : null}
      </div>
    </section>
  );
}
